import React, { useCallback, useEffect, useState, FormEvent } from 'react';
import axiosInstance from 'config/axiosInstance';
import { PAGE_SIZE } from 'config/constant';
import DateRangeFilter from 'reusable/datefilterrange';
import Pagination from 'reusable/pagination';
import SortBy from 'reusable/sortby';
import { IconEdit } from '@tabler/icons-react';
import EditDialog from 'reusable/Dialog/editDialog';
import {
  DialogConfig,
  DialogField,
  DialogType,
  NewDataField,
} from 'reusable/Dialog/dialogInterface';
import { editAttendanceFields } from '../../../Attendance/AttendanceField';
import { useTranslation } from 'react-i18next';
import Attendance from 'components/Attendance';

const sortingOptions = [
  { label: 'Default', value: '' },
  { label: 'Check-in Time', value: 'check_in' },
];

interface DriverWorkingAttendanceTableProps {
  id?: string;
}

interface DriverWorkingAttendance {
  id: string;
  check_in: string;
  check_out: string;
}

const DriverWorkingAttendanceTable: React.FC<
  DriverWorkingAttendanceTableProps
> = ({ id }) => {
  const { t, i18n } = useTranslation('driver');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortValue, setSortValue] = useState('');
  const [count, setCount] = useState(0);
  const [driverWorkingAttendance, setDriverWorkingAttendance] = useState<
    DriverWorkingAttendance[]
  >([]);
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState<DialogConfig>({
    title: '',
    subtitle: '',
    fields: [],
  });
  const [currentAttendanceId, setCurrentAttendanceId] = useState<string | null>(
    null
  );

  const handleSortChange = (sortValue: string) => {
    setSortValue(sortValue);
    setCurrentPage(1);
  };

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setDateRange({ startDate, endDate });
  };

  const formatDate = (datetime: string) => {
    if (!datetime) {
      return '-';
    }
    const date = new Date(datetime);
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const day = date.getDate();
    const monthIndex = date.getMonth();
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    if (i18n.language === 'jp') {
      return `${year}/${(monthIndex + 1).toString().padStart(2, '0')}/${day
        .toString()
        .padStart(2, '0')} | ${hours}:${minutes}`;
    } else {
      return `${day < 10 ? '0' + day : day} ${
        months[monthIndex]
      } ${year} | ${hours}:${minutes}`;
    }
  };

  const calculateDuration = (checkIn: string, checkOut: string) => {
    const checkInDate = new Date(checkIn);
    const checkOutDate = new Date(checkOut);

    if (checkOutDate.getTime() > checkInDate.getTime()) {
      const durationMs = checkOutDate.getTime() - checkInDate.getTime();
      const durationMins = Math.floor(durationMs / 60000);
      const hours = Math.floor(durationMins / 60);
      const minutes = durationMins % 60;

      return `${hours} hrs ${minutes} mins`;
    } else {
      return '-';
    }
  };

  const fetchData = useCallback(async () => {
    try {
      let apiUrl = '/v1/attendances/?';
      if (dateRange.startDate && dateRange.endDate) {
        apiUrl += `check_in__gte=${dateRange.startDate}T00%3A00%3A00.000000Z&check_in__lte=${dateRange.endDate}T23%3A59%3A59.999999Z&`;
      }
      apiUrl += `order_by=${sortValue}&page=${currentPage}&page_size=${PAGE_SIZE}&user=${id}`;
      const response = await axiosInstance.get(apiUrl);
      setDriverWorkingAttendance(response.data.results);
      setCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch data', error);
    }
  }, [id, currentPage, sortValue, dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openDialog = (
    title: string,
    subtitle: string,
    fields: DialogField[],
    dialogType: DialogType
  ) => {
    console.log('Opening dialog with fields:', fields); // Debugging line
    setDialogProps({ title, subtitle, fields });
    if (dialogType === DialogType.Edit) setIsEditDialogOpen(true);
  };

  const closeDialog = () => {
    setIsEditDialogOpen(false);
    setCurrentAttendanceId(null);
  };

  const handleDialog = async (
    dialogType: DialogType,
    attendance: DriverWorkingAttendance
  ) => {
    console.log('DialogType:', dialogType, 'Attendance:', attendance); // Debugging line
    if (dialogType === DialogType.Edit) {
      try {
        setCurrentAttendanceId(attendance.id);
        openDialog(
          'Edit Attendance Record',
          '',
          editAttendanceFields(t, attendance),
          DialogType.Edit
        );
      } catch (error) {
        console.error('Failed to fetch attendance data', error);
      }
    }
  };

  const handleEditAttendance = async (
    e: FormEvent<Element>,
    updatedData: NewDataField & { id: string }
  ) => {
    e.preventDefault();

    const combineDateTime = (date: any, time: any): string => {
      const combinedDateTime = new Date(`${date}T${time}:00.000Z`);
      return combinedDateTime.toISOString();
    };

    const checkInDateTime = combineDateTime(
      updatedData.check_in_date,
      updatedData.check_in_time
    );
    const checkOutDateTime = combineDateTime(
      updatedData.check_out_date,
      updatedData.check_out_time
    );

    const formattedUpdatedData = {
      check_in: checkInDateTime,
      check_out: checkOutDateTime,
    };

    try {
      if (updatedData) {
        const response = await axiosInstance.patch(
          `/v1/attendances/${updatedData.id}/`,
          formattedUpdatedData
        );

        setDriverWorkingAttendance((prevData) =>
          prevData.map((attendance) =>
            attendance.id === updatedData.id ? response.data : attendance
          )
        );
      }

      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Failed to update attendance:', error);
    }
  };
console.log(driverWorkingAttendance);
  return (
    <section className="flex flex-col space-y-2">
      <div className="flex flex-row space-x-2 items-center justify-between">
        <SortBy
          options={sortingOptions}
          defaultValue={sortValue}
          onChange={handleSortChange}
        />
        <DateRangeFilter onDateRangeChange={handleDateRangeChange} />
      </div>

      <div>
        <div className="flex flex-row bg-primary/50 rounded-md py-2 px-4">
          <h6 className="flex-1 text-center">
            {t('view.working-attendance.check-in')}
          </h6>
          <h6 className="flex-1 text-center">
            {t('view.working-attendance.check-out')}
          </h6>
          <h6 className="flex-1 text-center">
            {t('view.working-attendance.duration')}
          </h6>
          <h6 className="flex-1 text-center">
            {t('view.working-attendance.action')}
          </h6>
        </div>
        <div>
          {driverWorkingAttendance.map((attendance, index) => (
            <div
              key={index}
              className="grid grid-cols-4 gap-4 px-4 py-3 border-b items-center"
            >
              <div className="flex justify-center">
                {formatDate(attendance.check_in)}
              </div>
              <div className="flex justify-center">
                {formatDate(attendance.check_out)}
              </div>
              <div className="flex justify-center">
                {calculateDuration(attendance.check_in, attendance.check_out)}
              </div>
              <div className="flex justify-center">
                <IconEdit
                  size={20}
                  stroke={1.5}
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDialog(DialogType.Edit, attendance);
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <EditDialog
        isOpen={isEditDialogOpen}
        onClose={closeDialog}
        handleSubmit={(e: any, updatedData: any) =>
          handleEditAttendance(e, {
            ...updatedData,
            id: currentAttendanceId ?? '',
          })
        }
        confirmLabel={t('edit.save')}
        cancelLabel={t('edit.cancel')}
        title={dialogProps.title}
        subtitle={dialogProps.subtitle ?? ''}
        fields={dialogProps.fields}
        attendanceId={currentAttendanceId ?? ''}
      />

      <div>
        <Pagination
          allRows={count}
          rowsPerPage={PAGE_SIZE}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </section>
  );
};

export default DriverWorkingAttendanceTable;
